/* eslint-disable no-nested-ternary */
/* eslint-disable react/prop-types */
import React, { useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { Box, useMediaQuery } from "@mui/material";
import Typography from "../../../components/common/new/Typography/Typography";
import Dashboard from "../../../services/api/dashboard";
import "./subscriptioncounter.styles.css";

// ─── Mini Pill ────────────────────────────────────────────────────────────────
function MiniPill({ label, value, color, isMobile }) {
  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "3px",
        background: `${color}12`,
        border: `1.5px solid ${color}35`,
        borderRadius: "10px",
        padding: isMobile ? "6px 8px" : "8px 12px",
        minWidth: isMobile ? "52px" : "62px",
        flex: 1,
      }}
    >
      <Typography
        style={{
          fontSize: isMobile ? "16px" : "20px",
          fontWeight: 800,
          color,
          lineHeight: 1,
          display: "block",
        }}
      >
        {value || 0}
      </Typography>
      <Typography
        style={{
          fontSize: isMobile ? "9px" : "10px",
          fontWeight: 600,
          color: `${color}cc`,
          lineHeight: 1.2,
          display: "block",
          letterSpacing: "0.3px",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

// ─── Plus / Equal Symbol ──────────────────────────────────────────────────────
function OpSymbol({ char, isMobile }) {
  return (
    <Box
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: isMobile ? "14px" : "18px",
        fontWeight: 700,
        color: "#bbb",
        flexShrink: 0,
        paddingTop: "2px",
        userSelect: "none",
      }}
    >
      {char}
    </Box>
  );
}

// ─── Total Result Box ─────────────────────────────────────────────────────────
function TotalBox({ label, value, color, isMobile }) {
  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "3px",
        background: color,
        borderRadius: "10px",
        padding: isMobile ? "6px 10px" : "8px 14px",
        minWidth: isMobile ? "58px" : "70px",
        flexShrink: 0,
        boxShadow: `0 3px 10px ${color}66`,
      }}
    >
      <Typography
        style={{
          fontSize: isMobile ? "16px" : "20px",
          fontWeight: 800,
          color: "#fff",
          lineHeight: 1,
          display: "block",
        }}
      >
        {value || 0}
      </Typography>
      <Typography
        style={{
          fontSize: isMobile ? "8px" : "9px",
          fontWeight: 700,
          color: "rgba(255,255,255,0.85)",
          lineHeight: 1.2,
          display: "block",
          letterSpacing: "0.4px",
          textTransform: "uppercase",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

// ─── Subscription Card ────────────────────────────────────────────────────────
function SubscriptionCard({
  title,
  rowData,
  membershipTotal,
  accentColor,
  isMobile,
  isTablet,
}) {
  const subTotal = rowData.subscriptionCount || 0;
  const current = rowData.inProgressSubscriptions || 0;
  const newSubs = rowData.newSubscriptions || 0;
  const renewals = rowData.renewedSubscriptions || 0;

  // % with 1 decimal — e.g. 5.3%
  const pct =
    membershipTotal > 0
      ? Math.min((subTotal / membershipTotal) * 100, 100).toFixed(1)
      : "0.0";

  const cardLabel = title === "Value Creation Subscription" ? "Total" : "Total";

  return (
    <Box
      style={{
        background: "#fff",
        borderRadius: isMobile ? "14px" : "18px",
        padding: isMobile ? "14px 12px" : isTablet ? "16px" : "20px 20px",
        width: "100%",
        boxSizing: "border-box",
        boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
        border: "1px solid rgba(0,0,0,0.05)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Top accent strip */}
      <Box
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "4px",
          background: `linear-gradient(90deg, ${accentColor}55, ${accentColor})`,
          borderRadius: "18px 18px 0 0",
        }}
      />

      {/* ── Title Row with ACHIEVED % on right (image jaisa) ── */}
      <Box
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          paddingTop: "6px",
          marginBottom: isMobile ? "14px" : "16px",
        }}
      >
        {/* Left: Title + Membership Total */}
        <Box>
          <Typography
            size={isMobile ? "13px" : "15px"}
            weight={700}
            color="#2a2a2a"
            style={{ display: "block", marginBottom: "3px" }}
          >
            {title}
          </Typography>
          {/* <Typography
            style={{
              fontSize: isMobile ? "10px" : "11px",
              fontWeight: 500,
              color: "#aaa",
              display: "block",
            }}
          >
            Membership:{" "}
            <span style={{ color: "#4A5568", fontWeight: 700 }}>
              {membershipTotal || 0}
            </span>
          </Typography> */}
        </Box>

        {/* Right: ACHIEVED + big % — same as image */}
        <Box style={{ textAlign: "right", flexShrink: 0, marginLeft: "8px" }}>
          <Typography
            style={{
              fontSize: isMobile ? "8px" : "9px",
              fontWeight: 700,
              color: "#959292",
              letterSpacing: "1px",
              textTransform: "uppercase",
              display: "block",
              marginBottom: "1px",
            }}
          >
            SUBSCRIBED
          </Typography>
          <Typography
            style={{
              fontSize: isMobile ? "22px" : "20px",
              fontWeight: 900,
              color: accentColor,
              lineHeight: 1,
              display: "block",
            }}
          >
            {pct}%
          </Typography>
        </Box>
      </Box>

      {/* ── Pills Row: Current + New + Renewals = Total ── */}
      <Box
        style={{
          display: "flex",
          alignItems: "center",
          gap: isMobile ? "4px" : "6px",
          marginBottom: isMobile ? "14px" : "16px",
          flexWrap: "nowrap",
        }}
      >
        <MiniPill
          label="Current"
          value={current}
          color="#5C6BC0"
          isMobile={isMobile}
        />
        <OpSymbol char="+" isMobile={isMobile} />
        <MiniPill
          label="New"
          value={newSubs}
          color="#26C6A6"
          isMobile={isMobile}
        />
        <OpSymbol char="+" isMobile={isMobile} />
        <MiniPill
          label="Renewals"
          value={renewals}
          color="#EF7C7C"
          isMobile={isMobile}
        />
        <OpSymbol char="=" isMobile={isMobile} />
        <TotalBox
          label={cardLabel}
          value={subTotal}
          color={accentColor}
          isMobile={isMobile}
        />
      </Box>

      {/* ── Bold highlighted Progress Bar ── */}
      <Box>
        <Box
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "6px",
          }}
        >
          {/* <Typography
            style={{
              fontSize: isMobile ? "10px" : "11px",
              fontWeight: 700,
              color: "#555",
              letterSpacing: "0.3px",
              textTransform: "uppercase",
              display: "block",
            }}
          >
            Subscription Coverage
          </Typography>
          <Box
            style={{
              background: accentColor,
              borderRadius: "20px",
              padding: isMobile ? "2px 8px" : "3px 10px",
            }}
          >
            <Typography
              style={{
                fontSize: isMobile ? "10px" : "12px",
                fontWeight: 800,
                color: "#fff",
                display: "block",
              }}
            >
              {pct}%
            </Typography>
          </Box> */}
        </Box>

        {/* Thick progress track */}
        <Box
          style={{
            height: isMobile ? "10px" : "6px",
            borderRadius: "99px",
            background: "#EDF2F7",
            overflow: "hidden",
            boxShadow: "inset 0 1px 3px rgba(0,0,0,0.08)",
          }}
        >
          <Box
            style={{
              height: "100%",
              width: `${pct}%`,
              borderRadius: "99px",
              background: `linear-gradient(90deg, ${accentColor}99, ${accentColor})`,
              boxShadow: `0 2px 8px ${accentColor}66`,
              transition: "width 1.2s cubic-bezier(0.25,1,0.5,1)",
              position: "relative",
            }}
          >
            <Box
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "50%",
                background: "rgba(255,255,255,0.3)",
                borderRadius: "99px 99px 0 0",
              }}
            />
          </Box>
        </Box>

        <Typography
          style={{
            fontSize: isMobile ? "9px" : "10px",
            color: "#a5a3a3",
            marginTop: "5px",
            fontWeight: 700,
            display: "block",
          }}
        >
          {subTotal} of {membershipTotal} members subscribed
        </Typography>
      </Box>
    </Box>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
function SubscriptionCounter() {
  const [vcData, setVcData] = useState(null);
  const [fdtData, setFdtData] = useState(null);
  const [membershipTotal, setMembershipTotal] = useState(0);
  const isMobile = useMediaQuery("(max-width:600px)");
  const isTablet = useMediaQuery("(max-width:768px)");

  const subscriptionCounterVC = async () => {
    try {
      const response = await Dashboard.getSubscriptionReportVC({ type: 1 });
      setVcData(response.data || {});
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const subscriptionCounterFDT = async () => {
    try {
      const response = await Dashboard.getSubscriptionReportVC({ type: 3 });
      setFdtData(response.data || {});
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const fetchMembershipTotal = async () => {
    try {
      const response = await Dashboard.getMembershipByDivision();
      const apiData =
        response?.data?.data ||
        (Array.isArray(response?.data) ? response.data : null) ||
        (Array.isArray(response) ? response : []);
      const grand = Array.isArray(apiData)
        ? apiData.reduce((sum, item) => sum + (item.memberCount || 0), 0)
        : 0;
      setMembershipTotal(grand);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  useEffect(() => {
    subscriptionCounterVC();
    subscriptionCounterFDT();
    fetchMembershipTotal();
  }, []);

  return (
    <Box
      style={{
        width: "100%",
        maxWidth: "100%",
        overflow: "hidden",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: isMobile ? "12px" : "14px",
      }}
    >
      <SubscriptionCard
        title="Value Creation Subscription"
        rowData={vcData || {}}
        membershipTotal={membershipTotal}
        accentColor="#5C6BC0"
        isMobile={isMobile}
        isTablet={isTablet}
      />
      <SubscriptionCard
        title="Soka Successors"
        rowData={fdtData || {}}
        membershipTotal={membershipTotal}
        accentColor="#00BCD4"
        isMobile={isMobile}
        isTablet={isTablet}
      />
    </Box>
  );
}

export default SubscriptionCounter;
